import { createContext, useContext, useState, useCallback } from 'react';
import { authApi, setTokens, clearTokens } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(() => {
    try { return JSON.parse(localStorage.getItem('lavex_user')); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const login = useCallback(async (identifier, password) => {
    setLoading(true);
    setError('');
    try {
      const data = await authApi.login({ identifier, password });
      const u = data.data;

      // ── FILTRE ADMIN ───────────────────────────────────────────────────────
      // Seul le rôle "admin" est autorisé sur cette plateforme.
      if (u.role !== 'admin') {
        setError('Accès refusé. Cette plateforme est réservée aux administrateurs LAVEX uniquement.');
        setLoading(false);
        return { success: false };
      }

      setTokens(u.accessToken, u.refreshToken);
      localStorage.setItem('lavex_user', JSON.stringify(u));
      setUser(u);
      setLoading(false);

      // ── v2.2 : si mot de passe temporaire → rediriger avant accès ─────────
      if (u.password_reset_required) {
        return { success: true, passwordResetRequired: true };
      }

      return { success: true };
    } catch (err) {
      setError(err.message || 'Identifiants invalides ou serveur inaccessible.');
      setLoading(false);
      return { success: false };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const rt = localStorage.getItem('lavex_refresh_token');
      if (rt) await authApi.logout({ refreshToken: rt });
    } catch {}
    clearTokens();
    setUser(null);
  }, []);

  const updatePassword = useCallback(async (currentPassword, newPassword) => {
    await authApi.updatePassword({ currentPassword, newPassword });
    // v2.2: après changement, on déconnecte (le backend invalide les tokens)
    clearTokens();
    setUser(null);
  }, []);

  // ── Permet de rafraîchir les données user (ex: après changement de profil) ─
  const refreshUser = useCallback((newData) => {
    const updated = { ...user, ...newData };
    localStorage.setItem('lavex_user', JSON.stringify(updated));
    setUser(updated);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      setError,
      login,
      logout,
      updatePassword,
      refreshUser,
      // Expose le flag pour les routes
      passwordResetRequired: user?.password_reset_required === true,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
